import { useContext, useState } from 'react';
import toast from 'react-hot-toast';

import { LabourerContext } from '../../contexts/LabourerContext';
import UploadLicenseForm from '../UploadLicenseForm';
import Modal from '../Modal';
import Loader from '../Loader';

import licenseService from '../../services/licenseService';

import useLicenses from '../../hooks/useLicenses';
import useLicenseTypes from '../../hooks/useLicenseTypes';

import { CheckBadgeIcon } from '@heroicons/react/20/solid';

const OnboardingLicenses = ({ onSubmit }) => {
  const { labourer } = useContext(LabourerContext);

  const [selectedLicenseType, setSelectedLicenseType] = useState(null);

  const [isUploadLicenseModalOpen, setIsUploadLicenseModalOpen] = useState(false);
  const [isUploadingLicense, setIsUploadingLicense] = useState(false);

  const { data: licenseTypesData, isLoading: isLoadingLicenseTypes } = useLicenseTypes({
    page: 1,
    perPage: 1000
  });

  const licenseTypes = licenseTypesData?.licenseTypes;

  const { data: labourerLicenseData, mutate: mutateLicenses } = useLicenses({ labourerId: labourer._id, page: 1, perPage: 1000 });
  const labourerLicenses = labourerLicenseData?.licenses;

  const handleUploadLicense = async (licenseData) => {
    if (isUploadingLicense) return;

    licenseData.labourer = labourer._id;

    try {
      setIsUploadingLicense(true);

      await licenseService.createLicense({ licenseData });
      await mutateLicenses();

      setIsUploadLicenseModalOpen(false);

      toast.success('License uploaded');
    } catch (error) {
      console.log(error);
      toast.error('An error occurred while uploading the license');
    } finally {
      setIsUploadingLicense(false);
    }
  };

  const handleUploadLicenseClicked = (licenseType) => {
    setSelectedLicenseType(licenseType);
    setIsUploadLicenseModalOpen(true);
  }

  const handleSubmit = () => {
    onSubmit();
  }

  return (
    <>
      <p className='text-lg text-center mb-4'>
        Please upload all your relevant work licenses.
      </p>

      {isLoadingLicenseTypes ? (
        <div className='py-8 flex items-center justify-center'>
          <Loader />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4 mb-4">
            {licenseTypes?.map(licenseType => (
              <div key={`license-${licenseType.hrwlCode}`} className='flex items-center justify-between border border-gray-300 rounded-md px-4 py-2'>
                <div className='flex flex-col gap-2 '>
                  <div key={licenseType._id} className='flex items-center'>
                    <div className='flex items-center gap-2'>
                      <p className='bg-ase-blue rounded-md text-white font-semibold p-1 text-sm'>
                        {licenseType.hrwlCode}
                      </p>

                      <p className='font-medium'>{licenseType.name}</p>
                    </div>
                  </div>

                  <p className='text-sm pr-4'>{licenseType.description}</p>
                </div>

                <button
                  onClick={e => handleUploadLicenseClicked(licenseType)}
                  className={`text-white bg-ase-blue font-medium text-sm px-4 py-2 rounded-md w-[80px] flex items-center justify-center ${labourerLicenses?.find(l => l.type.hrwlCode === licenseType.hrwlCode) ? 'bg-green-400 text-white' : ''}`}
                >
                  {labourerLicenses?.find(l => l.type.hrwlCode === licenseType.hrwlCode) ? <CheckBadgeIcon className='h-5 w-5' /> : 'Upload'}
                </button>
              </div>
            ))}
          </div>

          <div className='flex gap-2'>
            <button
              onClick={handleSubmit}
              className='bg-ase-blue h-[48px] px-4 font-semibold text-white rounded-md'
            >
              Next
            </button>
          </div>
        </>
      )}

      <Modal
        isOpen={isUploadLicenseModalOpen}
        onClose={e => setIsUploadLicenseModalOpen(false)}
        overflowType='visible'
      >
        <UploadLicenseForm
          licenseType={selectedLicenseType}
          isUploading={isUploadingLicense}
          onSubmit={handleUploadLicense}
          onCloseClicked={e => setIsUploadLicenseModalOpen(false)}
        />
      </Modal>
    </>
  );
}

export default OnboardingLicenses;
